import MuiDialog, {
  dialogClasses,
  DialogProps as MuiDialogProps,
} from '@mui/material/Dialog';
import { Breakpoint, SxProps, Theme } from '@mui/material/styles';
import { useCallback } from 'react';

// Reserve space for the docked "Ask Tread" bar so a centered dialog — and the primary CTA in
// its footer — never renders underneath it. Shrinking the flex centering container lifts the
// whole (maxHeight-clamped, centered) paper above the bar. The variable is 0px unless the bar
// is mounted, so this is a no-op otherwise. See src/tread-agent/TreadAgentDock.tsx.
const reserveDockSpaceSx: SxProps<Theme> = {
  [`& .${dialogClasses.container}`]: {
    height: 'calc(100% - var(--tread-agent-dock-height, 0px))',
  },
};

// React propagates synthetic events through the component tree, not the DOM tree, so a click
// on the portal-rendered dialog (backdrop included) still reaches the onClick handlers of the
// JSX ancestors that declared it — e.g. an expandable table row.
const stopClickPropagation: NonNullable<MuiDialogProps['onClick']> = (event) => {
  event.stopPropagation();
};

type DialogProps = {
  isOpen: boolean;
  onClose?: () => void;
  preventCloseOnBackdropClick?: boolean;
  preventCloseOnEscapeKeyDown?: boolean;
  sx?: SxProps<Theme>;
  maxWidth?: Breakpoint | false;
};

export default function Dialog({
  children,
  isOpen,
  onClose,
  preventCloseOnBackdropClick,
  preventCloseOnEscapeKeyDown,
  sx,
  maxWidth,
}: React.PropsWithChildren<DialogProps>) {
  const handleClose = useCallback<Exclude<MuiDialogProps['onClose'], undefined>>(
    (_event, reason) => {
      const shouldCloseOnBackdropClick =
        reason === 'backdropClick' && !preventCloseOnBackdropClick;
      const shouldCloseEscapeKeyDown =
        reason === 'escapeKeyDown' && !preventCloseOnEscapeKeyDown;

      if (shouldCloseOnBackdropClick || shouldCloseEscapeKeyDown) {
        onClose?.();
      }
    },
    [onClose, preventCloseOnBackdropClick, preventCloseOnEscapeKeyDown],
  );

  return (
    <MuiDialog
      fullWidth
      maxWidth={maxWidth}
      onClick={stopClickPropagation}
      onClose={handleClose}
      open={isOpen}
      sx={[reserveDockSpaceSx, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {children}
    </MuiDialog>
  );
}
